import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ChatCircleText, Clock, Star, WarningCircle } from "@phosphor-icons/react";
import {
  EmptyState,
  ReviewCard,
  ReviewSkeleton,
  ReviewStat,
} from "@/components/therapist/reviews/TherapistReviewCards";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { therapistReplySchema } from "@/schemas/reviews";
import type { TherapistReview } from "@/types";

export const Route = createFileRoute("/dashboard/therapist/reviews")({
  component: TherapistReviewsPage,
});

export async function readTherapistReviewsResponse(res: Response): Promise<TherapistReview[]> {
  const body = await res.json().catch(() => null) as { items?: unknown; error?: unknown } | null;

  if (!res.ok) {
    const message = typeof body?.error === "string" ? body.error : "Recenzije nije moguće učitati.";
    throw new Error(message);
  }

  if (!Array.isArray(body?.items)) {
    throw new Error("Recenzije nije moguće učitati.");
  }

  return body.items as TherapistReview[];
}

function TherapistReviewsPage() {
  const [items, setItems] = useState<TherapistReview[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isSavingReply, setIsSavingReply] = useState(false);

  async function load() {
    setError(null);
    setIsLoading(true);
    try {
      const res = await fetch("/api/me/therapist/reviews");
      setItems(await readTherapistReviewsResponse(res));
    } catch (err) {
      setItems([]);
      setError(err instanceof Error ? err.message : "Recenzije nije moguće učitati.");
    } finally {
      setIsLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function submitReply(id: string) {
    // Ista shema kao na serveru; tekst ostaje u polju dok spremanje ne uspije.
    const body = { reply: replyText };
    const parsed = therapistReplySchema.safeParse(body);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Odgovor nije ispravan.");
      return;
    }
    setIsSavingReply(true);
    try {
      const res = await fetch(`/api/me/therapist/reviews/${id}/reply`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      await throwIfNotOk(res, "Odgovor nije sačuvan. Pokušajte ponovo.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Odgovor nije sačuvan. Pokušajte ponovo.");
      return;
    } finally {
      setIsSavingReply(false);
    }
    setEditing(null);
    setReplyText("");
    toast.success("Odgovor je sačuvan.");
    await load();
  }

  const reviewCount = items.length;
  const averageRating =
    reviewCount > 0
      ? items.reduce((sum, item) => sum + item.rating, 0) / reviewCount
      : null;
  const pendingCount = items.filter((item) => item.status === "pending").length;

  return (
    <div className="relative mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      <div className="min-w-0">
        <h1
          className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Recenzije
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Pratite ocjene klijenata i odgovorite na recenzije koje su prošle administrativni pregled.
        </p>
      </div>

      {error ? (
        <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50/70 px-4 py-3 text-sm text-rose-800">
          <WarningCircle className="mt-0.5 size-4 shrink-0" weight="fill" />
          {error}
        </div>
      ) : null}

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ReviewStat
          icon={Star}
          iconClass="text-yellow-500"
          label="Prosječna ocjena"
          value={averageRating == null ? "—" : averageRating.toFixed(1)}
          hint={reviewCount > 0 ? `${reviewCount} recenzija` : "Još nema ocjena"}
        />
        <ReviewStat
          icon={ChatCircleText}
          iconClass="text-primary"
          label="Ukupno recenzija"
          value={String(reviewCount)}
          hint="Sve recenzije klijenata"
        />
        <ReviewStat
          icon={Clock}
          iconClass="text-muted-foreground/60"
          label="Čeka pregled"
          value={String(pendingCount)}
          hint="Javno tek nakon odobrenja"
          accent={pendingCount > 0}
        />
      </section>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 items-center gap-3">
          <ChatCircleText className="size-5 shrink-0 text-primary" />
          <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
            Povratne informacije
          </h2>
          {isLoading && <div className="size-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />}
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card px-5 py-3 shadow-sm sm:px-6 sm:py-4">
        {isLoading ? (
          <ReviewSkeleton />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          items.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              isEditing={editing === review.id}
              replyText={replyText}
              onEdit={() => { setEditing(review.id); setReplyText(review.therapistReply ?? ""); }}
              onReply={() => { setEditing(review.id); setReplyText(""); }}
              onReplyTextChange={setReplyText}
              onCancel={() => { setEditing(null); setReplyText(""); }}
              onSave={() => submitReply(review.id)}
              isSaving={isSavingReply && editing === review.id}
            />
          ))
        )}
      </div>
    </div>
  );
}
